import { readCookie, setCookie } from "@/lib/cookie";

export const BRIGHTNESS_STORAGE_KEY = "displayBrightness";
export const TIME_ZONE_STORAGE_KEY = "displayTimeZone";
export const CHART_TIME_ZONE_STORAGE_KEY = "chartTimeZone";
export const WORKSPACE_BACKGROUND_STYLE_STORAGE_KEY =
  "workspaceBackgroundStyle";
export const WORKSPACE_BACKGROUND_BASE_STORAGE_KEY = "workspaceBackgroundBase";
export const WORKSPACE_BACKGROUND_ACCENT_STORAGE_KEY =
  "workspaceBackgroundAccent";
export const DEFAULT_DISPLAY_BRIGHTNESS = 1;
export const DEFAULT_DISPLAY_TIME_ZONE = "America/New_York";
export const DEFAULT_CHART_TIME_ZONE = "America/New_York";
export const DEFAULT_WORKSPACE_BACKGROUND_STYLE = "dark";
export const DEFAULT_WORKSPACE_BACKGROUND_BASE = "#101216";
export const DEFAULT_WORKSPACE_BACKGROUND_ACCENT = "#5ED8FF";

export const DISPLAY_TIME_ZONES = [
  "local",
  "UTC",
  "America/New_York",
  "America/Los_Angeles",
  "America/Chicago",
  "Europe/London",
  "Europe/Paris",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Singapore",
] as const;

export type DisplayTimeZone = (typeof DISPLAY_TIME_ZONES)[number];
export const CHART_TIME_ZONES = DISPLAY_TIME_ZONES;
export type ChartTimeZonePreference = (typeof CHART_TIME_ZONES)[number];
export const WORKSPACE_BACKGROUND_STYLES = ["dark", "light"] as const;
export type WorkspaceBackgroundStylePreference =
  (typeof WORKSPACE_BACKGROUND_STYLES)[number];

export interface WorkspaceBackgroundPreference {
  accentColor: string;
  baseColor: string;
  style: WorkspaceBackgroundStylePreference;
}

export function normalizeBrightness(value: unknown): number {
  const n =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value)
        : DEFAULT_DISPLAY_BRIGHTNESS;
  if (!Number.isFinite(n)) return DEFAULT_DISPLAY_BRIGHTNESS;
  return Math.min(1.25, Math.max(0.75, Math.round(n * 100) / 100));
}

export function normalizeTimeZone(value: unknown): DisplayTimeZone {
  return DISPLAY_TIME_ZONES.includes(value as DisplayTimeZone)
    ? (value as DisplayTimeZone)
    : DEFAULT_DISPLAY_TIME_ZONE;
}

export function normalizeChartTimeZone(
  value: unknown,
): ChartTimeZonePreference {
  return CHART_TIME_ZONES.includes(value as ChartTimeZonePreference)
    ? (value as ChartTimeZonePreference)
    : DEFAULT_CHART_TIME_ZONE;
}

export function normalizeWorkspaceBackgroundStyle(
  value: unknown,
): WorkspaceBackgroundStylePreference {
  if (value === "solid" || value === "vertical" || value === "organic") {
    return "dark";
  }
  return WORKSPACE_BACKGROUND_STYLES.includes(
    value as WorkspaceBackgroundStylePreference,
  )
    ? (value as WorkspaceBackgroundStylePreference)
    : DEFAULT_WORKSPACE_BACKGROUND_STYLE;
}

export function normalizeHexColor(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  if (!/^#?[0-9a-fA-F]{6}$/.test(trimmed)) return fallback;
  return `#${trimmed.replace(/^#/, "").toUpperCase()}`;
}

export function readStoredBrightness(): number {
  if (typeof window === "undefined") return DEFAULT_DISPLAY_BRIGHTNESS;
  return normalizeBrightness(
    window.localStorage.getItem(BRIGHTNESS_STORAGE_KEY) ??
      readCookie(BRIGHTNESS_STORAGE_KEY),
  );
}

export function readStoredTimeZone(): DisplayTimeZone {
  if (typeof window === "undefined") return DEFAULT_DISPLAY_TIME_ZONE;
  return normalizeTimeZone(
    window.localStorage.getItem(TIME_ZONE_STORAGE_KEY) ??
      readCookie(TIME_ZONE_STORAGE_KEY),
  );
}

// Charts share the display setting; legacy chartTimeZone values are superseded.
export function readStoredChartTimeZone(): ChartTimeZonePreference {
  return readStoredTimeZone();
}

export function resolveDisplayTimeZone(timeZone: string): string {
  return timeZone === "local"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
    : timeZone;
}

export function readStoredWorkspaceBackgroundPreference(): WorkspaceBackgroundPreference {
  if (typeof window === "undefined") {
    return {
      accentColor: DEFAULT_WORKSPACE_BACKGROUND_ACCENT,
      baseColor: DEFAULT_WORKSPACE_BACKGROUND_BASE,
      style: DEFAULT_WORKSPACE_BACKGROUND_STYLE,
    };
  }

  return {
    accentColor: normalizeHexColor(
      window.localStorage.getItem(WORKSPACE_BACKGROUND_ACCENT_STORAGE_KEY),
      DEFAULT_WORKSPACE_BACKGROUND_ACCENT,
    ),
    baseColor: normalizeHexColor(
      window.localStorage.getItem(WORKSPACE_BACKGROUND_BASE_STORAGE_KEY),
      DEFAULT_WORKSPACE_BACKGROUND_BASE,
    ),
    style: normalizeWorkspaceBackgroundStyle(
      window.localStorage.getItem(WORKSPACE_BACKGROUND_STYLE_STORAGE_KEY),
    ),
  };
}

export function applyBrightness(value: number) {
  if (typeof document === "undefined") return;
  const brightness = normalizeBrightness(value);
  document.documentElement.style.setProperty(
    "--display-brightness",
    String(brightness),
  );
  document.documentElement.classList.toggle(
    "display-brightness-adjusted",
    brightness !== DEFAULT_DISPLAY_BRIGHTNESS,
  );
}

export function persistBrightness(value: number) {
  const brightness = normalizeBrightness(value);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(BRIGHTNESS_STORAGE_KEY, String(brightness));
  }
  setCookie(BRIGHTNESS_STORAGE_KEY, String(brightness));
  applyBrightness(brightness);
}

export function persistTimeZone(value: unknown) {
  const timeZone = normalizeTimeZone(value);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(TIME_ZONE_STORAGE_KEY, timeZone);
  }
  setCookie(TIME_ZONE_STORAGE_KEY, timeZone);
  window.dispatchEvent(new Event("timezonechange"));
}

export function persistChartTimeZone(value: unknown) {
  persistTimeZone(value);
}

export function persistWorkspaceBackgroundPreference(
  value: Partial<WorkspaceBackgroundPreference>,
) {
  const current = readStoredWorkspaceBackgroundPreference();
  const next: WorkspaceBackgroundPreference = {
    accentColor: normalizeHexColor(
      value.accentColor,
      current.accentColor ?? DEFAULT_WORKSPACE_BACKGROUND_ACCENT,
    ),
    baseColor: normalizeHexColor(
      value.baseColor,
      current.baseColor ?? DEFAULT_WORKSPACE_BACKGROUND_BASE,
    ),
    style: normalizeWorkspaceBackgroundStyle(value.style ?? current.style),
  };

  if (typeof window !== "undefined") {
    window.localStorage.setItem(
      WORKSPACE_BACKGROUND_STYLE_STORAGE_KEY,
      next.style,
    );
    window.localStorage.setItem(
      WORKSPACE_BACKGROUND_BASE_STORAGE_KEY,
      next.baseColor,
    );
    window.localStorage.setItem(
      WORKSPACE_BACKGROUND_ACCENT_STORAGE_KEY,
      next.accentColor,
    );
    window.dispatchEvent(new Event("workspacebackgroundchange"));
  }

  return next;
}
